import { useCallback, useEffect, useRef, useState } from "react";
import {
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  updateProfile,
  type Auth,
  type User,
} from "firebase/auth";
import { auth as defaultAuth } from "@/config/firebaseConfig";
import {
  clearPendingEmail,
  readLocalDraft,
  readPendingEmail,
  writeLocalDraft,
  writePendingEmail,
} from "@/lib/onboarding/local-draft";

/**
 * Passwordless account creation for the onboarding (GRA-171): Firebase's
 * email-link sign-in. The link returns to `/onboarding?s=account&verify=1`;
 * on that return the address is read back from the browser that sent it, or
 * asked for when the link was opened on another device.
 */
export type EmailLinkPhase =
  | "form"
  | "sent"
  | "confirmEmail"
  | "verifying"
  | "error";

export type EmailLinkErrorKey =
  | "expiredLink"
  | "invalidLink"
  | "email"
  | "sendFailed"
  | "signInFailed";

export interface EmailLinkAccount {
  name: string;
  email: string;
  mobile: string;
}

export const RESEND_COOLDOWN_SECONDS = 30;
export const EMAIL_LINK_RETURN_PATH = "/onboarding?s=account&verify=1";

export function mapEmailLinkError(error: unknown): EmailLinkErrorKey {
  const code = (error as { code?: string } | null)?.code ?? "";
  switch (code) {
    case "auth/expired-action-code":
      return "expiredLink";
    case "auth/invalid-action-code":
      return "invalidLink";
    case "auth/invalid-email":
    case "auth/missing-email":
      return "email";
    default:
      return "signInFailed";
  }
}

/** Removes Firebase's one-time parameters once the link has been consumed. */
export function stripEmailLinkParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of ["verify", "oobCode", "mode", "apiKey", "lang", "continueUrl"]) {
    next.delete(key);
  }
  return next;
}

/**
 * Default `href`, hoisted so it is one stable reference across renders: a
 * fresh closure here would make `complete` unstable (it depends on `href`),
 * which would re-run the returning-link effect below on every render and
 * could reset an in-flight sign-in back to "confirmEmail".
 */
const defaultHref = () => window.location.href;

interface UseEmailLinkAuthOpts {
  /** Called once with the signed-in user after a link completes. */
  onSignedIn: (user: User, account: Partial<EmailLinkAccount>) => void;
  auth?: Auth;
  /** The page URL; injectable for tests. */
  href?: () => string;
}

export function useEmailLinkAuth({
  onSignedIn,
  auth = defaultAuth,
  href = defaultHref,
}: UseEmailLinkAuthOpts) {
  const [phase, setPhase] = useState<EmailLinkPhase>("form");
  const [email, setEmail] = useState<string>(() => readLocalDraft().account?.email ?? "");
  const [error, setError] = useState<EmailLinkErrorKey | null>(null);
  const [resendAvailableIn, setResendAvailableIn] = useState(0);
  const onSignedInRef = useRef(onSignedIn);
  onSignedInRef.current = onSignedIn;
  // The default `href` is a fresh arrow function on every render, same as any
  // inline caller that doesn't memoize it. Read it through a ref so `complete`
  // and the returning-link effect below don't pick up a new identity on every
  // render - otherwise the effect re-runs after each state update (e.g. right
  // after an error) and resets the phase it just set.
  const hrefRef = useRef(href);
  hrefRef.current = href;
  const completing = useRef(false);

  const complete = useCallback(
    async (address: string, fallbackAccount?: Pick<EmailLinkAccount, "name" | "mobile">) => {
      if (completing.current) return;
      completing.current = true;
      setPhase("verifying");
      setError(null);
      try {
        // On the device that sent the link, the local draft already has the
        // name and mobile. On another device there is no draft, so the
        // confirm-email screen collects them directly - write them
        // unconditionally so a retry after a failed sign-in isn't stuck with
        // the stale values from the first attempt.
        if (fallbackAccount) {
          writeLocalDraft({
            account: { ...fallbackAccount, email: address.trim() },
          });
        }
        const credential = await signInWithEmailLink(auth, address.trim(), hrefRef.current());
        const account: Partial<EmailLinkAccount> = readLocalDraft().account ?? {};
        const name = account.name?.trim();
        if (name && !credential.user.displayName) {
          try {
            await updateProfile(credential.user, { displayName: name });
          } catch {
            // Non-critical: the name is also sent with business.create.
          }
        }
        clearPendingEmail();
        onSignedInRef.current(credential.user, { ...account, email: address.trim() });
      } catch (err) {
        setError(mapEmailLinkError(err));
        setPhase("error");
      } finally {
        completing.current = false;
      }
    },
    [auth],
  );

  // A returning link: finish the sign-in, or ask for the address when this
  // browser did not send it. Runs once per `auth`/`complete` identity, not on
  // every render, so it doesn't clobber a phase the user already moved past.
  useEffect(() => {
    const current = hrefRef.current();
    if (!isSignInWithEmailLink(auth, current)) return;
    const pending = readPendingEmail();
    if (pending) {
      void complete(pending);
    } else {
      setPhase("confirmEmail");
    }
  }, [auth, complete]);

  useEffect(() => {
    if (resendAvailableIn <= 0) return;
    const id = window.setInterval(
      () => setResendAvailableIn((seconds) => Math.max(0, seconds - 1)),
      1000,
    );
    return () => window.clearInterval(id);
  }, [resendAvailableIn]);

  const sendLink = useCallback(
    async (account: EmailLinkAccount) => {
      setError(null);
      const address = account.email.trim();
      try {
        await sendSignInLinkToEmail(auth, address, {
          url: `${window.location.origin}${EMAIL_LINK_RETURN_PATH}`,
          handleCodeInApp: true,
        });
        writePendingEmail(address);
        writeLocalDraft({
          account: { ...account, email: address },
          linkSentAt: new Date().toISOString(),
        });
        setEmail(address);
        setPhase("sent");
        setResendAvailableIn(RESEND_COOLDOWN_SECONDS);
      } catch (err) {
        const code = (err as { code?: string } | null)?.code;
        setError(code === "auth/invalid-email" ? "email" : "sendFailed");
        setPhase("form");
      }
    },
    [auth],
  );

  const resend = useCallback(async () => {
    const account = readLocalDraft().account;
    if (!account || resendAvailableIn > 0) return;
    await sendLink({ ...account, email: email || account.email });
  }, [email, resendAvailableIn, sendLink]);

  const confirmEmail = useCallback(
    async (address: string, fallbackAccount?: Pick<EmailLinkAccount, "name" | "mobile">) => {
      setEmail(address.trim());
      await complete(address, fallbackAccount);
    },
    [complete],
  );

  const backToForm = useCallback(() => {
    setPhase("form");
    setError(null);
  }, []);

  return {
    phase,
    email,
    error,
    resendAvailableIn,
    sendLink,
    resend,
    confirmEmail,
    backToForm,
  };
}
